import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/molecules/phone";
import { useCreateMember } from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useTranslation } from "@/features/utils/i18n";
import { UserPlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * Add somebody to the team without inviting them.
 *
 * A name is the only required field, and that is the point: most of a salon's
 * team never sign in, and until members and accesses were separate objects the
 * only way to create one was to e-mail an invitation to a person who does not
 * exist. Everything else here is editable afterwards from the row.
 */
export const CreateMemberDialog = () => {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [roleText, setRoleText] = useState("");
  const [phone, setPhone] = useState("");

  const createMember = useCreateMember();
  const isOwner = useHasRole("OWNER");

  if (!isOwner) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !name.trim()) return;

    try {
      await createMember.mutateAsync({
        clientId,
        name: name.trim(),
        role_text: roleText.trim() || undefined,
        phone: phone || undefined,
      });
      toast.success(t("messages.memberCreated"));
      setName("");
      setRoleText("");
      setPhone("");
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.createError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <UserPlusIcon className="h-4 w-4 mr-2" weight="fill" />
          {t("actions.addMember")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("createMember.title")}</DialogTitle>
            <DialogDescription>
              {t("createMember.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="member-name">{t("createMember.name")}</Label>
              <Input
                id="member-name"
                value={name}
                placeholder={t("createMember.namePlaceholder")}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                {t("createMember.nameHelp")}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="member-role-text">
                {t("createMember.roleText")}
              </Label>
              <Input
                id="member-role-text"
                value={roleText}
                placeholder={t("createMember.roleTextPlaceholder")}
                onChange={(e) => setRoleText(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="member-phone">{t("createMember.phone")}</Label>
              <PhoneInput
                id="member-phone"
                value={phone}
                onChange={(value) => setPhone(value || "")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("editProfile.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createMember.isPending || !name.trim()}
            >
              {createMember.isPending
                ? t("editProfile.saving")
                : t("createMember.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
